import React, { useState, useEffect } from "react";
import { httpService } from "../../services/httpService";
import LoaderSpiner from "../../components/LoaderSpiner/LoaderSpiner";
import { FaUndo, FaRupeeSign, FaBox, FaTruck, FaCalendarAlt, FaInfoCircle,FaPlus } from "react-icons/fa";
import AdjustmentDrawer from "./AdjustmentDrawer";

const ProductExpiryReturn = ({ productId , inventoryId}) => {
  const [returns, setReturns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState({
    totalReturns: 0,
    totalQuantity: 0,
    totalAmount: 0,
    uniqueSuppliers: 0
  });

  const [showAdjustmentDrawer, setShowAdjustmentDrawer] = useState(false);

  useEffect(() => {
    if (productId) {
      fetchReturnHistory();
    }
  }, [productId]);

  const fetchReturnHistory = async () => {
    setLoading(true);
    try {
      const response = await httpService.get(`/vendorproduct/return-history/${productId}`);
      if (response.code === 200) {
        setReturns(response.data);
        
        // Calculate summary
        const totalQuantity = response.data.reduce((sum, item) => sum + (item.quantity || 0), 0);
        const totalAmount = response.data.reduce((sum, item) => sum + (item.refundAmount || 0), 0);
        const uniqueSuppliers = new Set(response.data.map(item => item.supplierId)).size;
        
        setSummary({
          totalReturns: response.data.length,
          totalQuantity: totalQuantity,
          totalAmount: totalAmount,
          uniqueSuppliers: uniqueSuppliers
        });
      }
    } catch (err) {
      console.error("Error fetching return history:", err);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 2
    }).format(amount || 0);
  };

  if (loading) return <LoaderSpiner text="Loading return history..." />;

  return (
    <div className="p-2 bg-white rounded-lg shadow">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold text-gray-800">Product Return History</h2>
        <div className="flex gap-2 items-center">
        {returns.length > 0 && (
            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-semibold">
              Total Returns: {summary.totalReturns}
            </span>
        )}
        <button
      onClick={() => setShowAdjustmentDrawer(true)}
      className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md shadow text-sm"
    >
      <FaPlus className="w-3 h-3" /> Add Adjustment
    </button>
  </div>
</div>

      {/* Summary Cards */}
      {returns.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-gradient-to-r from-blue-500 to-blue-600 rounded-lg p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Total Returns</p>
                <p className="text-2xl font-bold">{summary.totalReturns}</p>
              </div>
              <FaUndo className="w-8 h-8 opacity-80" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-green-500 to-green-600 rounded-lg p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Total Quantity</p>
                <p className="text-2xl font-bold">{summary.totalQuantity}</p>
              </div>
              <FaBox className="w-8 h-8 opacity-80" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Total Refund Amount</p>
                <p className="text-2xl font-bold">{formatCurrency(summary.totalAmount)}</p>
              </div>
              <FaRupeeSign className="w-8 h-8 opacity-80" />
            </div>
          </div>
          
          <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-lg p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm opacity-90">Suppliers</p>
                <p className="text-2xl font-bold">{summary.uniqueSuppliers}</p>
              </div>
              <FaTruck className="w-8 h-8 opacity-80" />
            </div>
          </div>
        </div>
      )}

      {returns.length === 0 ? (
        <div className="text-center py-12">
          <FaUndo className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <p className="text-gray-500 text-lg">No return history available for this product.</p>
            <p className="text-gray-400 text-sm mt-2">Supplier returns for this product will appear here</p>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <FaCalendarAlt className="w-3 h-3" />
                    Return Date
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <FaTruck className="w-3 h-3" />
                    Supplier
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <FaBox className="w-3 h-3" />
                    Batch / SKU
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Quantity Returned
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Unit Price
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <FaRupeeSign className="w-3 h-3" />
                    Refund Amount
                  </div>
                </th>
                {/* <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <div className="flex items-center gap-2">
                    <FaInfoCircle className="w-3 h-3" />
                    Reason
                  </div>
                </th> */}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {returns.map((item, idx) => (
                <tr key={idx} className="hover:bg-gray-50 transition-colors duration-200">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatDate(item.returnDate)}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div>
                      <div className="font-medium">{item.supplierName || 'N/A'}</div>
                      {item.supplierPhone && (
                        <div className="text-xs text-gray-500">{item.supplierPhone}</div>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">
                    <div>
                      {/* <div className="font-mono text-xs">{item.batchNumber || 'N/A'}</div> */}
                      <div className="text-xs text-gray-500">SKU: {item.skuNumber || '-'}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    <span className="px-2 py-1 bg-red-100 text-red-800 rounded-full text-xs font-semibold">
                      {item.quantity}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {formatCurrency(item.unitPrice)}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-semibold text-gray-900">
                    {formatCurrency(item.refundAmount)}
                  </td>
                  {/* <td className="px-6 py-4 text-sm text-gray-600 max-w-xs">
                    {item.reason || 'Not specified'}
                  </td> */}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <AdjustmentDrawer
        isOpen={showAdjustmentDrawer}
        onClose={() => setShowAdjustmentDrawer(false)}
        productId={productId}
        inventoryId={inventoryId}
        onSaved={fetchReturnHistory}
      />
    </div>
  );
};

export default ProductExpiryReturn;